import { type FC, Fragment } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';

// MUI
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';

// screen
import { BalanceCostModel, Day } from 'modules/map/components/balance-cost/types';
import { NORMAL_FONT_SIZE } from 'modules/map/components/pdf-print-form/utils';
import SizedText from 'modules/map/components/pdf-print-form/styled-components/SizedText';
import PdfWeekday from 'modules/map/components/pdf-print-form/styled-components/PdfWeekday';
import {
  checkIfNumber,
  getMaxRowCounts,
  isNonEatingDay,
} from 'modules/map/components/balance-cost/utils';

const DashedLine = () => {
  return (
    <div
      style={{
        borderBottom: '1px dashed #000',
        width: '100%',
        position: 'absolute',
        left: 0,
        bottom: 0,
        height: 0,
      }}
    />
  );
};

const RowInDay = ({
  day,
  dayIndex,
  rowsCount,
  showAfternoon,
  flattenAfternoon,
}: {
  day: Day;
  dayIndex: number;
  rowsCount: number;
  showAfternoon: boolean;
  flattenAfternoon: string[];
}) => {
  const item = day.items[dayIndex];
  const additionCharge = day.additionCharges[dayIndex];
  const afternoon = flattenAfternoon[dayIndex];
  const borderStyles = {
    borderBottom: `1px solid ${dayIndex === rowsCount - 1 ? 'auto' : 'transparent'}!important`,
    position: 'relative',
    p: 0,
  };

  const noteFormatting = () => {
    const dayNote = day.note || '';
    const shouldBreakNotes = dayNote.includes('\n');

    return shouldBreakNotes
      ? dayNote.split('\n').map((note, index) => (
          <SizedText key={index} sx={{ fontSize: NORMAL_FONT_SIZE }}>
            {note}
          </SizedText>
        ))
      : dayNote;
  };

  const row = {
    isFistRow: dayIndex === 0,
    dish: day.dishes[dayIndex],
    name: item?.name || '',
    quantity: checkIfNumber(item?.quantity) ? item.quantity : '',
    unit: item?.unit || '',
    price: checkIfNumber(item?.price) ? item.price : '',
    raw: item?.raw || '',
    additionChargeName: additionCharge?.name || '',
    additionChargePrice: checkIfNumber(additionCharge?.price) ? additionCharge.price : '',
    afternoon,
    note: noteFormatting(),
  };

  const showDashesLine = () => {
    return dayIndex !== rowsCount - 1 && <DashedLine />;
  };

  return (
    <TableRow>
      {/* THỨ/NGÀY */}
      {row.isFistRow && <PdfWeekday beBold datetime={day.datetime} rowSpan={rowsCount} />}

      {/* THỰC ĐƠN */}
      <TableCell sx={borderStyles}>
        <SizedText sx={{ fontSize: NORMAL_FONT_SIZE }}>
          {isNonEatingDay(day) ? <FormattedMessage id="map.noBoardingMeal" /> : row.dish}
        </SizedText>
        {showDashesLine()}
      </TableCell>

      {/* CHI TIẾT */}
      <TableCell sx={borderStyles}>
        <SizedText sx={{ fontSize: NORMAL_FONT_SIZE }}>{row.name}</SizedText>
        {showDashesLine()}
      </TableCell>

      {/* SL(g) */}
      <TableCell sx={borderStyles}>
        <SizedText sx={{ fontSize: NORMAL_FONT_SIZE, textAlign: 'right' }}>
          {checkIfNumber(row.quantity) && (
            <FormattedNumber
              value={row.quantity as number}
              minimumFractionDigits={1}
              maximumFractionDigits={1}
            />
          )}
        </SizedText>
        {showDashesLine()}
      </TableCell>

      {/* ĐG/Kg */}
      <TableCell sx={borderStyles}>
        <SizedText sx={{ fontSize: NORMAL_FONT_SIZE, textAlign: 'right' }}>{row.unit}</SizedText>
        {showDashesLine()}
      </TableCell>

      {/* TT */}
      <TableCell sx={borderStyles}>
        <SizedText sx={{ fontSize: NORMAL_FONT_SIZE, textAlign: 'right' }}>
          {checkIfNumber(row.price) && <FormattedNumber value={row.price as number} />}
        </SizedText>
        {showDashesLine()}
      </TableCell>

      {/* TP Chín */}
      <TableCell sx={borderStyles}>
        <SizedText sx={{ fontSize: NORMAL_FONT_SIZE }}>{row.raw}</SizedText>
        {showDashesLine()}
      </TableCell>

      {/* Chi phí phụ */}
      <TableCell sx={borderStyles}>
        <SizedText sx={{ fontSize: NORMAL_FONT_SIZE }}>{row.additionChargeName}</SizedText>
        {showDashesLine()}
      </TableCell>

      {/* Số tiền */}
      <TableCell sx={borderStyles}>
        <SizedText sx={{ fontSize: NORMAL_FONT_SIZE, textAlign: 'right' }}>
          {checkIfNumber(row.additionChargePrice) && (
            <FormattedNumber value={row.additionChargePrice as number} />
          )}
        </SizedText>
        {showDashesLine()}
      </TableCell>

      {/* GHI CHÚ / QUÀ CHIỀU */}
      {showAfternoon ? (
        <TableCell sx={borderStyles}>
          <SizedText sx={{ fontSize: NORMAL_FONT_SIZE }}>{row.afternoon}</SizedText>
        </TableCell>
      ) : (
        row.isFistRow && (
          <TableCell
            sx={{
              position: 'relative',
              p: 0,
            }}
            rowSpan={rowsCount}
          >
            <SizedText sx={{ fontSize: NORMAL_FONT_SIZE }}>{day.note}</SizedText>
          </TableCell>
        )
      )}

      {/* Thuế GTGT */}
      <TableCell sx={borderStyles}>{showDashesLine()}</TableCell>

      {/* Tổng tiền */}
      <TableCell sx={borderStyles}>{showDashesLine()}</TableCell>
    </TableRow>
  );
};

const TotalInDay = ({
  prices,
  showAfternoon,
}: {
  prices: Day['price'];
  showAfternoon: boolean;
}) => {
  return (
    <>
      <style>
        {`
          @media print {
            body {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }

            .print-row {
              background-color: #F0F0F0 !important;
            }

            .print-row td {
              font-weight: 700 !important;
            }
          }
        `}
      </style>
      <TableRow>
        <TableCell />
        <TableCell />
        <TableCell>
          <SizedText sx={{ fontWeight: 600 }}>
            *<FormattedMessage id="map.totalQuantity" />
          </SizedText>
        </TableCell>

        <TableCell />
        <TableCell />
        <TableCell>
          <SizedText sx={{ fontSize: NORMAL_FONT_SIZE, textAlign: 'right' }}>
            {prices?.itemPrice && <FormattedNumber value={prices.itemPrice} />}
          </SizedText>
        </TableCell>

        <TableCell />
        <TableCell />
        <TableCell>
          <SizedText sx={{ fontSize: NORMAL_FONT_SIZE, textAlign: 'right' }}>
            {prices.additionPrice && <FormattedNumber value={prices.additionPrice} />}
          </SizedText>
        </TableCell>
        <TableCell>
          {showAfternoon && prices?.afternoon && <FormattedNumber value={prices?.afternoon} />}
        </TableCell>
        <TableCell>
          <SizedText sx={{ fontSize: NORMAL_FONT_SIZE, textAlign: 'right' }}>
            {prices?.tax && <FormattedNumber value={prices.tax} />}
          </SizedText>
        </TableCell>
        <TableCell>
          <SizedText sx={{ fontSize: NORMAL_FONT_SIZE, textAlign: 'right' }}>
            {prices?.total && <FormattedNumber value={prices.total} />}
          </SizedText>
        </TableCell>
      </TableRow>
    </>
  );
};

interface PdfTableBodyProp {
  data: BalanceCostModel;
}

const PdfTableBody: FC<PdfTableBodyProp> = ({ data }) => {
  const days = data.structuredData?.data.items || [];
  const showAfternoon = data.structuredData.metadata.hasAfternoon;

  return (
    <TableBody>
      {days.map((day, idx) => {
        // Flatten afternoonMeal to a single array of strings
        const flattenAfternoon: string[] = Array.isArray(day.afternoons)
          ? day.afternoons.flat()
          : [];

        const rowsCount = getMaxRowCounts({
          ingredients: day.items.length,
          dishes: day.dishes.length,
          additionCharges: day.additionCharges.length,
          afternoons: flattenAfternoon.length,
        });

        return (
          <Fragment key={idx}>
            {Array.from({ length: rowsCount }).map((_, index) => {
              return (
                <RowInDay
                  key={index}
                  day={day}
                  dayIndex={index}
                  rowsCount={rowsCount}
                  showAfternoon={showAfternoon}
                  flattenAfternoon={flattenAfternoon}
                />
              );
            })}
            <TotalInDay prices={day.price} showAfternoon={showAfternoon} />
          </Fragment>
        );
      })}
    </TableBody>
  );
};

export default PdfTableBody;
